import React, { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Media,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { useNavigate } from "react-router-dom";
import { MainHeader, TextApp, DropdownMenuItem } from "./styled.components";
import "assets/css/sidemenu/index.css";
import { Icon, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import Search from "@mui/icons-material/Search";
// @ts-ignore
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { requestReports } from "actions/report";
import { getViews } from "actions/vista";
import { getOperationTypes } from "actions/tipoOperacion";
import { setThemeMode } from "actions/user";
import { ListMenu } from "./ListMenu/ListMenu";
import { ListMenuMobile } from "./ListMenuMobile/ListMenuMobile";


// Custom hook para obtener el state global necesario
const useSideMenuState = () => {
  return useSelector((state: any) => ({
    user: state.user,
    menu: state.common.menu,
    theme: state.theme,
    reportsData: state.reportsData,
    viewReportData: state.viewReportData,
    operationTypeData: state.operationTypeData,
  }));
};

// Función utilitaria para obtener el menú de búsqueda
const getSearchMenu = (modules: any) => {
  const result: Array<any> = [];
  if (!modules) return result;
  const traverse = (items: any[]) => {
    items.forEach((item) => {
      if ([3, 4].includes(item.idTipoVista)) {
        result.push({
          id: item.idVista,
          name: item.descripcionMenu,
          url: item.url,
        });
      }
      if (item.hijos && item.hijos.length) {
        traverse(item.hijos);
      }
    });
  };
  traverse(modules);
  return result;
};

const SideMenu = () => {
  const history = useNavigate();
  const dispatch: Dispatch = useDispatch();
  const {
    user: { fullName, modo, logoFinal, imagen, modules },
    menu,
    theme: { header, menuLateralTheme },
    reportsData,
    viewReportData,
    operationTypeData,
  } = useSideMenuState();

  const [openMobil, setOpenMobil] = useState(false);
  const [openFav, setOpenFav] = useState(false);
  const [openClock, setOpenClock] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [searchMenu, setSearchMenu] = useState<Array<any>>([]);
  const [menuIndex, setMenuIndex] = useState(0);

  // Actualiza el menú de búsqueda cuando cambian los módulos
  useEffect(() => {
    setSearchMenu(getSearchMenu(modules));
  }, [modules]);

  // Carga inicial de datos: solo se ejecuta una vez al montar el componente
  useEffect(() => {
    if (!reportsData?.reports) {
      requestReports().then((res) => {
        dispatch({ type: "SET_REPORTS", payload: res });
      });
    }
    if (!viewReportData?.viewList) {
      getViews(0, "0").then((res) => {
        dispatch({ type: "SET_VIEW_LIST", payload: res.filter((item: any) => item.idEstatus === 1) });
      });
    }
    if (!viewReportData?.reportList) {
      getViews(0, "1,2,3").then((res) => {
        dispatch({ type: "SET_REPORT_LIST", payload: res.filter((item: any) => item.idEstatus === 1) });
      });
    }
    if (!operationTypeData?.operationTypeList) {
      getOperationTypes(0).then((res) => {
        dispatch({ type: "SET_OPERATION_TYPE_LIST", payload: res?.filter((item: any) => item.idEstatus === 1) });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Utilidad para transformar módulos a estructura de menú recursiva
  const buildMenuTree = (items: any[]): any[] => {
    if (!items) return [];
    return items.map((item: any) => ({
      id: item.idVista,
      text: item.descripcion,
      module: item.url,
      children: buildMenuTree(item.hijos),
    }));
  };

  // Renderizado recursivo de menú
  const renderMenuTree = (items: any[]) => {
    if (!items) return null;
    return items.map((item: any) => {
      const url =
        item.module === "SSRS" || item.module === "PowerBI"
          ? `../../report/${item.id}/${item.module}`
          : `../../${item.module}`;
      return (
        <li key={item.id}>
          <div>
            <a href={url}>{item.text}</a>
          </div>
          {(item.module === "SSRS" || item.module === "PowerBI") && (
            <img
              src={"/commons/link-plus.svg"}
              onClick={() => {
                const win = window.open(url, "_blank");
                if (win != null) win.focus();
              }}
            />
          )}
          {item.children && item.children.length > 0 && (
            <ul>{renderMenuTree(item.children)}</ul>
          )}
        </li>
      );
    });
  };

  // Menú principal con paginación
  const handleMenu = () => {
    const menus = buildMenuTree(modules);
    const width = window.innerWidth;
    const itemsLimit = width < 1200 ? 4 : width < 1600 ? 5 : 6;
    let initIndex = menuIndex;
    let lastIndex = initIndex + itemsLimit;

    if (width < 780) {
      return <>{renderMenuTree(menus)}</>;
    }
    return (
      <>
        {menus.length > itemsLimit && menuIndex > 0 && (
          <li>
            <div onClick={() => setMenuIndex(menuIndex - 1)}>
              <Icon>arrow_back_ios</Icon>
            </div>
          </li>
        )}
        {renderMenuTree(menus.slice(initIndex, lastIndex))}
        {menus.length > itemsLimit && menuIndex + itemsLimit < menus.length && (
          <li>
            <div onClick={() => setMenuIndex(menuIndex + 1)}>
              <Icon>arrow_forward_ios</Icon>
            </div>
          </li>
        )}
      </>
    );
  };

  const handleOnSelect = (item: any) => {
    if (item) {
      let url =
        item.url === "SSRS" || item.url === "PowerBI"
          ? `../../report/${item.id}/${item.url}`
          : `../../${item.url}`;
      history(url);
    }
  };



  // Renderiza menú móvil parametrizable (favoritos, recientes, etc.)
  const renderMobileMenu = (menuId: number, openClass: string, isOpen: boolean) => {
    if (!menu || !menu.length) return null;
    const myMenu = menu.find((item: any) => item.id === menuId);
    if (!myMenu) return null;
    return (
      <div className={`lista-menu ${isOpen ? openClass : ""}`}>
        <div style={{ backgroundColor: menuLateralTheme?.headBg }}>
          <span style={{ color: menuLateralTheme?.headTextColor }}>
            {myMenu.text}
          </span>
        </div>
        <div
          className="body"
          style={{ backgroundColor: menuLateralTheme?.bgColor }}
        >
          <ul>
            {myMenu.children &&
              myMenu.children.map((item: any) => (
                <li key={item.id}>
                  <span style={{ color: menuLateralTheme?.textColor }}>
                    {item?.text}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderFilter = () => {
    return (
      <ReactSearchAutocomplete items={searchMenu} onSelect={handleOnSelect} />
    );
  };
  const toggleDrawer = (flag: any) => (event: any) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenMobil(flag);
  };

  return (
    <MainHeader bg={header?.background}>
      {renderMobileMenu(1, "open-fav", openFav)}
      {renderMobileMenu(2, "open-clock", openClock)}
      <div className={"logo-nav"}>
        <img alt={"#"} src={logoFinal} />
        <span className={"separator"} />

        <TextApp color={header?.textApp}>Portal BI</TextApp>
      </div>
      <div className={`items-nav site-navbar`}>
        {!openMobil ? (
          <ListMenu />
        ) : (
          <ListMenuMobile open={openMobil} toggleDrawer={toggleDrawer} />
        )}
      </div>
      <div className={"control-users"}>
        <div className={`input-search ${openSearch ? "open-search" : ""}`}>
          {renderFilter()}
        </div>
        <IconButton
          className={"mobile-search"}
          onClick={() => {
            setOpenSearch(!openSearch);
            setOpenClock(false);
            setOpenFav(false);
            setOpenMobil(false);
          }}
        >
          {openSearch ? (
            <CloseIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          ) : (
            <Search
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          )}
        </IconButton>
        <IconButton
          className={"mobile-later"}
          onClick={() => {
            setOpenClock(!openClock);
            setOpenFav(false);
            setOpenMobil(false);
            setOpenSearch(false);
          }}
        >
          {openClock ? (
            <CloseIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          ) : (
            <WatchLaterIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          )}
        </IconButton>
        <IconButton
          className={"mobile-fav"}
          onClick={() => {
            setOpenFav(!openFav);
            setOpenClock(false);
            setOpenMobil(false);
            setOpenSearch(false);
          }}
        >
          {openFav ? (
            <CloseIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          ) : (
            <FavoriteIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          )}
        </IconButton>

        <IconButton
          className={"mobile-Menu"}
          onClick={() => {
            setOpenMobil(!openMobil);
            setOpenFav(false);
            setOpenClock(false);
            setOpenSearch(false);
          }}
        >
          {openMobil ? (
            <CloseIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          ) : (
            <MenuIcon
              className={"menuH"}
              style={{ color: menuLateralTheme?.headBg }}
            />
          )}
        </IconButton>

        <UncontrolledDropdown
          nav
          className={"panel-user"}
          style={{ backgroundColor: header?.background }}
        >
          <DropdownToggle
            className="pr-0"
            nav
            style={{ backgroundColor: header?.background }}
          >
            <Media className="align-items-center">
              <span className="avatar avatar-sm rounded-circle">
                <img
                  alt="..."
                  src={
                    imagen
                      ? imagen
                      : `${process.env.PUBLIC_URL}/avatars/default.png`
                  }
                />
              </span>
              <Media>
                <span
                  className="text-panel-user"
                  style={{ color: header?.textColor }}
                >
                  &nbsp;
                  {(fullName &&
                    fullName.substring(
                      0,
                      fullName.length > 10 ? 8 : fullName.length
                    )) ||
                    "Not name"}
                </span>
              </Media>
            </Media>
          </DropdownToggle>
          <DropdownMenu
            right
            style={{
              backgroundColor: header?.background,
              border: "1px solid white",
            }}
          >
            <DropdownItem
              className={"text-panel-user-container"}
              onClick={() => {
                setThemeMode(modo === "claro" ? "oscuro" : "claro").then(
                  (res) => {
                    dispatch({
                      type: "SET_THEME_MODO",
                      modo: modo === "claro" ? "oscuro" : "claro",
                    });
                  }
                );
              }}
            >
              <DropdownMenuItem
                className={"text-panel-user"}
                color={header?.textColor}
                hoverColor={header?.hoverColor}
              >
                {modo === "claro" ? "Modo oscuro" : "Modo claro"}
              </DropdownMenuItem>
            </DropdownItem>
            <DropdownItem
              className={"text-panel-user-container"}
              onClick={() => {
                history("/profile");
              }}
            >
              <DropdownMenuItem
                className={"text-panel-user"}
                color={header?.textColor}
                hoverColor={header?.hoverColor}
              >
                Perfil
              </DropdownMenuItem>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem
              className={"text-panel-user-container"}
              onClick={async () => {
                //@ts-ignore
                history("/login");
                await dispatch({
                  type: "LOGOUT",
                });
                await dispatch({
                  type: "REMOVE_REPORTS",
                });
                await dispatch({
                  type: "REMOVE_VIEW_LIST",
                });
                await dispatch({
                  type: "REMOVE_OPERATION_TYPE_LIST",
                });
              }}
            >
              <DropdownMenuItem
                className={"text-panel-user"}
                color={header?.textColor}
                hoverColor={header?.hoverColor}
              >
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    </MainHeader>
  );
};

export default SideMenu;
